import { clearCookie, readCookie, writeCookie } from './cookies';

// Cookie + localStorage Write-Through fuer Client-Einstellungen (Theme/Font/Sprache).
//
// WARUM: Die native iOS-App (Capacitor, Origin capacitor://localhost) persistiert
// document.cookie NICHT auf die Platte — WKWebView schreibt nur echte HTTP(S)-Cookies
// in den Cookie-Store. Folge: rein cookie-basierte Einstellungen (ff_theme_*, ff_font,
// ff_lang) gehen bei jedem iOS-Kaltstart verloren ("Einstellungen nach OTA-Update
// zurueckgesetzt"). localStorage wird dagegen zuverlaessig persistiert.
//
// Der Cookie bleibt trotzdem der geteilte Kanal: Auf app.lorath.eu teilen sich SPA
// und Legacy-HTML-Seiten die Origin und lesen/schreiben Theme/Font/Sprache ueber
// dieselben Cookies. Deshalb: BEIDE Speicher schreiben, beim Lesen Cookie-first
// (localStorage nur als Absicherung gegen den iOS-Cookie-Verlust).
//
// Encoding-Paritaet: writeCookie speichert URI-encodiert und readCookie decodiert —
// in localStorage liegt der ROHE Wert, damit beide Lesepfade denselben Klartext liefern.

export type PrefStorage = Pick<Storage, 'getItem' | 'setItem'>;

function defaultStorage(): Storage | null {
  // Schon der ZUGRIFF auf window.localStorage kann werfen (Privatmodus /
  // blockierte Website-Daten) — defensiv kapseln.
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

// Leerstring zaehlt IMMER als "nicht gesetzt" (gleiche Semantik wie theme.tsx /
// das Inline-No-FOUC-Script in index.html).
function normalize(value: string | null): string | null {
  return value && value.length > 0 ? value : null;
}

export function readPref(
  name: string,
  cookieSource?: string,
  storage: PrefStorage | null = defaultStorage(),
): string | null {
  // (a) Cookie-first — geteilter Kanal mit den Legacy-HTML-Seiten.
  const fromCookie = normalize(readCookie(name, cookieSource ?? document.cookie));
  if (fromCookie !== null) return fromCookie;

  // (b) localStorage-Fallback — iOS-Kaltstart: Cookies weg, localStorage bleibt.
  if (storage) {
    try {
      const fromStorage = normalize(storage.getItem(name));
      if (fromStorage !== null) return fromStorage;
    } catch {
      // Privatmodus/Quota darf nie werfen — dann zaehlt der Speicher als leer.
    }
  }

  return null;
}

export function writePref(
  name: string,
  value: string,
  storage: PrefStorage | null = defaultStorage(),
): void {
  try {
    writeCookie(name, value);
  } catch {
    // z.B. document nicht verfuegbar — Cookie-Ausfall darf localStorage nicht blockieren.
  }
  if (storage) {
    try {
      // Bewusst der ROHE Wert (kein encodeURIComponent) — siehe Encoding-Paritaet oben.
      storage.setItem(name, value);
    } catch {
      // Privatmodus/Quota darf nie werfen.
    }
  }
}

export function clearPref(
  name: string,
  storage: Pick<Storage, 'removeItem'> | null = defaultStorage(),
): void {
  try {
    clearCookie(name);
  } catch {
    // z.B. document nicht verfuegbar — Cookie-Ausfall darf localStorage nicht blockieren.
  }
  if (storage) {
    try {
      storage.removeItem(name);
    } catch {
      // Privatmodus/Quota darf nie werfen.
    }
  }
}
